import { PipelineDetailsDto } from "@cradua/data";
import { createReducer, on, Action } from "@ngrx/store";
import { EntityState, EntityAdapter, createEntityAdapter } from "@ngrx/entity";

import * as PipelinedetailsActions from "./pipelinedetails.actions";

export const PIPELINEDETAILS_FEATURE_KEY = "pipelinedetails";

export interface PipelinedetailsState extends EntityState<PipelineDetailsDto> {
  selectedPipelinedetailsId?: string | number; // which Pipelinedetails record has been selected
  loaded: boolean; // has the Pipelinedetails list been loaded
  error?: string | null; // last none error (if any)
}

export interface PipelinedetailsPartialState {
  readonly [PIPELINEDETAILS_FEATURE_KEY]: PipelinedetailsState;
}

export const pipelinedetailsAdapter: EntityAdapter<PipelineDetailsDto> =
  createEntityAdapter<
    PipelineDetailsDto
  >({
    selectId: (Pipelinedetails) => Pipelinedetails.id,
    sortComparer: sortByName,
  });

export function sortByName(a: PipelineDetailsDto, b: PipelineDetailsDto): number {
  return new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime();
}

export const initialState: PipelinedetailsState = pipelinedetailsAdapter
  .getInitialState(
    {
      // set initial required properties
      loaded: false,
      error: null,
      selectedPipelinedetailsId: null,
    },
  );

const pipelinedetailsReducer = createReducer(
  initialState,
  on(PipelinedetailsActions.loadPipelinedetails, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    PipelinedetailsActions.loadPipelinedetailsSuccess,
    (state, { Pipelinedetails }) =>
      pipelinedetailsAdapter.setAll(Pipelinedetails, {
        ...state,
        loaded: false,
      }),
  ),
  on(PipelinedetailsActions.loadPipelinedetailsFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),
  on(PipelinedetailsActions.fetchPipelinedetailsRecord, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    PipelinedetailsActions.fetchPipelinedetailsRecordSuccess,
    (state, { record }) =>
      pipelinedetailsAdapter.setAll(record, {
        ...state,
        loaded: false,
      }),
  ),
  on(PipelinedetailsActions.fetchPipelinedetailsRecordFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),

  on(PipelinedetailsActions.fetchByIdPipelinedetailsRecord, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    PipelinedetailsActions.fetchByIdPipelinedetailsRecordSuccess,
    (state, { record }) =>
      pipelinedetailsAdapter.setAll(record, {
        ...state,
        loaded: false,
      }),
  ),
  on(PipelinedetailsActions.fetchByIdPipelinedetailsRecordFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),

  on(PipelinedetailsActions.addPipelinedetails, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    PipelinedetailsActions.addPipelinedetailsSuccess,
    (state, { newPipelinedetails }) =>
      pipelinedetailsAdapter.addOne(newPipelinedetails, {
        ...state,
        loaded: false,
      }),
  ),
  on(PipelinedetailsActions.addPipelinedetailsFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),
  on(PipelinedetailsActions.updatePipelinedetails, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    PipelinedetailsActions.updatePipelinedetailsSuccess,
    (state, { updatePipelinedetails }) =>
      pipelinedetailsAdapter.upsertOne(updatePipelinedetails, {
        ...state,
        loaded: false,
      }),
  ),
  on(PipelinedetailsActions.updatePipelinedetailsFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),
  // search correspondence

  on(PipelinedetailsActions.searchPipelinedetailsRecord, (state, { query }) => {
    return {
      ...state,
      selectedPipelinedetailsId: query,
      loaded: false,
      error: null,
    };
  }),
  on(PipelinedetailsActions.searchPipelinedetailsIdRecord, (state, { query }) => {
    return {
      ...state,
      selectedPipelinedetailsId: query,
      loaded: false,
      error: null,
    };
  }),
  on(PipelinedetailsActions.removePipelinedetails, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    PipelinedetailsActions.removePipelinedetailsSuccess,
    (state, { id }) =>
      pipelinedetailsAdapter.removeOne(id, { ...state, loaded: false }),
  ),
  on(PipelinedetailsActions.removePipelinedetailsFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),

  on(PipelinedetailsActions.clearPipelinedetails, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    PipelinedetailsActions.clearPipelinedetailsSuccess,
    (state, { Pipelinedetails }) =>
      pipelinedetailsAdapter.setAll(Pipelinedetails, {
        ...state,
        loaded: false,
      }),
  ),
);

export function reducer(state: PipelinedetailsState | undefined, action: Action) {
  return pipelinedetailsReducer(state, action);
}